import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, ArrowLeft, Inbox } from "lucide-react";

export default function HelmRoster() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);        // selected producer {id,name}
  const [hhs, setHhs] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => { const { data } = await api.get("/agent/roster"); setData(data); };
  useEffect(() => { load(); }, []);

  const openProducer = async (p) => {
    setSel(p); setHhs(null);
    const { data } = await api.get(`/agent/roster/${p.id}/households`);
    setHhs(data.households);
  };
  const reassign = async (householdId, producerId) => {
    if (!producerId) return;
    setBusy(householdId);
    try { await api.post(`/agent/households/${householdId}/assign`, { producer_id: producerId }); await openProducer(sel); await load(); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="p-6 sm:p-10 max-w-4xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  return (
    <div className="p-6 sm:p-10 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight" data-testid="roster-title">Advisor Roster</h1>
        <p className="text-muted-foreground mt-1.5">Workloads across the agency, and reassignment of households between advisors.</p>
      </div>

      {!sel ? (
        <>
          <div className="rounded-xl border-2 border-[#FDE68A] bg-[#FFFBEB] p-4 mb-4 flex items-center gap-3" data-testid="roster-unassigned">
            <Inbox className="w-5 h-5 text-[#92400E]" />
            <p className="text-sm text-[#92400E]"><b>{data.unassigned}</b> unassigned households — claim or assign them from the Action Dashboard queue.</p>
          </div>
          <div className="rounded-xl border-2 border-[#E5E5E0] bg-white divide-y" data-testid="roster-list">
            {data.producers.map((p) => (
              <button key={p.id} onClick={() => openProducer(p)} className="w-full text-left flex items-center gap-3 p-4 hover:bg-[#FBFBF9]" data-testid={`roster-producer-${p.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#0B192C] text-[#D4AF37] flex items-center justify-center font-bold text-sm shrink-0"><Users className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0B192C] truncate">{p.name}</p>
                  <p className="text-xs text-[#9CA3AF]">NPN {p.npn || "—"}</p>
                </div>
                <span className="text-sm font-bold text-[#0B192C]" data-testid={`roster-count-${p.id}`}>{p.assigned} households</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={() => setSel(null)} className="mb-3" data-testid="roster-back"><ArrowLeft className="w-4 h-4 mr-1" /> All advisors</Button>
          <h2 className="text-lg font-bold text-[#0B192C] mb-3">{sel.name} — {sel.assigned} households</h2>
          {!hhs ? <Skeleton className="h-64 w-full rounded-xl" /> : hhs.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-[#E5E5E0] bg-white p-8 text-center text-[#6B7280]">No households assigned.</div>
          ) : (
            <div className="rounded-xl border-2 border-[#E5E5E0] bg-white divide-y" data-testid="roster-households">
              {hhs.map((h) => (
                <div key={h.household_id} className="flex items-center gap-3 p-3" data-testid={`roster-hh-${h.household_id}`}>
                  <button onClick={() => navigate(`/agent/households/${h.household_id}`)} className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-semibold text-[#0B192C] truncate">{h.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{h.state || "—"}</p>
                  </button>
                  <select disabled={busy === h.household_id} onChange={(e) => reassign(h.household_id, e.target.value)} defaultValue=""
                    className="h-9 rounded-md border bg-white px-2 text-xs" data-testid={`roster-reassign-${h.household_id}`}>
                    <option value="">Reassign to…</option>
                    {data.producers.filter((p) => p.id !== sel.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
